import React from 'react'
import { Status, Substatus, UpdateStatusDialog } from '.'

interface State {
  readonly selectedStatus?: Status
  readonly updating: boolean
  readonly dialogOpen: boolean
}

interface UpdateStatusValues {
  readonly status: Status
  readonly substatus?: Substatus
  readonly reasoning?: string
}

interface ChildProps {
  readonly selectedStatus?: Status
  readonly onSelectStatus: (selectedStatus: Status) => void
  readonly dialogOpen: boolean
  readonly onDialogClose: () => void
}

interface Props {
  readonly organizationID: string
  readonly traceIDs: string[]
  readonly onDialogClose?: () => void
  readonly children: (props: ChildProps) => React.ReactNode
  readonly updateStatus: (values: UpdateStatusValues) => Promise<any>

  /**
   * If the current status is provided, the dialog workflow will not be
   * started if it is selected
   */
  readonly currentStatus?: Status
  readonly filters?: {
    readonly filterText: string
    readonly quickFilter: string
    readonly aggregations: Record<string, string[]>
  }
  readonly additionalFilters?: { readonly [key: string]: any }
  readonly isStatusApprovalEnabled: boolean
}

export class UpdateStatusManager extends React.Component<Props, State> {
  public state: State = {
    dialogOpen: false,
    updating: false
  }

  public render() {
    const { selectedStatus, updating, dialogOpen } = this.state
    const {
      children,
      organizationID,
      traceIDs,
      filters,
      additionalFilters,
      isStatusApprovalEnabled
    } = this.props

    return (
      <React.Fragment>
        {children({
          selectedStatus: dialogOpen || updating ? selectedStatus : undefined,
          onSelectStatus: this.onSelectStatus,
          dialogOpen,
          onDialogClose: this.onDialogClose
        })}
        {selectedStatus && dialogOpen && (
          <UpdateStatusDialog
            organizationID={organizationID}
            traceIDs={traceIDs}
            filters={filters}
            additionalFilters={additionalFilters}
            status={selectedStatus}
            isOpen={dialogOpen}
            onCancel={this.onDialogClose}
            onConfirm={this.onStatusUpdate}
            isStatusApprovalEnabled={isStatusApprovalEnabled}
          />
        )}
      </React.Fragment>
    )
  }

  private onSelectStatus = (selectedStatus: Status) => {
    const { currentStatus } = this.props
    if (!currentStatus || selectedStatus !== currentStatus) {
      this.setState({ selectedStatus, dialogOpen: true })
    }
  }

  private onDialogClose = () => {
    const { onDialogClose } = this.props
    if (onDialogClose) {
      onDialogClose()
    }
    this.setState({
      dialogOpen: false
    })
  }

  private onStatusUpdate = (additionalData: {
    readonly substatus?: Substatus
    readonly reasoning?: string
  }) => {
    const { selectedStatus: status } = this.state

    if (status) {
      this.setState({ updating: true, dialogOpen: false }, () => {
        this.props
          .updateStatus({
            status,
            ...additionalData
          })
          .finally(() => this.setState({ updating: false }))
      })
    }
  }
}
